import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale/pt-BR'

import type {
  AgentAdapterType,
  AgentStatus,
  EstimatedEffort,
  ProjectStatus,
  RagDocumentCategoria,
  RagDocumentConfidencialidade,
  RagDocumentDepartamento,
  RagDocumentStatus,
  RecommendationKind,
  RecommendationStatus,
  RoutineStatus,
  RunStatus,
  TaskOperationType,
  TaskStatus,
} from '@/types/api'

// Tokens semânticos da convenção (`convenccao/convenccao UI – Convenção de Cores.txt`).
// Use sempre `bg-{token}/20 text-{token} border-{token}/30` para badges (regra §7).
export type SemanticToken =
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info'
  | 'muted'
  | 'primary'

export const AGENT_STATUSES: AgentStatus[] = [
  'idle',
  'working',
  'offline',
  'errored',  // Wave 1B (AUDIT-001): era 'error', alinhado com DB CHECK
  'paused',
]

// Adapters expostos no filtro da tela /agents. O backend pode retornar
// outros valores (ex.: 'gemini', 'internal') — esses passam por `formatAdapterType`
// no display, mas não viram opção fixa no FilterSelect.
export const ADAPTER_TYPES: AgentAdapterType[] = [
  'claude_code',
  'codex',
  'shell',
  'webhook',
  'internal',
  'gemini',
]

export const agentStatusLabel: Record<AgentStatus, string> = {
  idle: 'Ocioso',
  working: 'Em execução',
  offline: 'Offline',
  errored: 'Erro',  // Wave 1B (AUDIT-001)
  paused: 'Pausado',
}

// Labels conhecidos por adapter. Como `AgentAdapterType` aceita string livre
// (provedores managed entram no DB sem CHECK), usamos `Partial` + helper para
// cair num fallback amigável quando aparece um ID novo.
const ADAPTER_TYPE_LABELS: Partial<Record<AgentAdapterType, string>> = {
  claude_code: 'Claude Code',
  codex: 'Codex',
  shell: 'Shell',
  webhook: 'Webhook',
  internal: 'Sistema',
  gemini: 'Gemini / Google',
  anthropic: 'Anthropic',
  ollama: 'Ollama',
  huggingface: 'HuggingFace',
}

export function formatAdapterType(adapter: AgentAdapterType): string {
  const label = ADAPTER_TYPE_LABELS[adapter]
  if (label) return label
  // Fallback: humaniza o id cru (`some_new_provider` → `Some New Provider`).
  return adapter
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** @deprecated use `formatAdapterType` — Record fixo não cobre adapter types novos. */
export const adapterTypeLabel: Partial<Record<AgentAdapterType, string>> =
  ADAPTER_TYPE_LABELS

// Mapa semântico de domínio: status do agente -> token oficial (§5/§6).
export const agentStatusToken: Record<AgentStatus, SemanticToken> = {
  idle: 'muted',
  working: 'success',
  offline: 'muted',
  errored: 'destructive',  // Wave 1B (AUDIT-001)
  paused: 'warning',
}

// Classe utilitária para o "status dot" (8x8) do AgentCard, usando tokens.
export const agentStatusDotClass: Record<AgentStatus, string> = {
  idle: 'bg-muted-foreground/60',
  working: 'bg-success animate-pulse',
  offline: 'bg-muted-foreground/40',
  errored: 'bg-destructive',  // Wave 1B (AUDIT-001)
  paused: 'bg-warning',
}

export const agentStatusTextClass: Record<AgentStatus, string> = {
  idle: 'text-muted-foreground',
  working: 'text-success',
  offline: 'text-muted-foreground/60',
  errored: 'text-destructive',  // Wave 1B (AUDIT-001)
  paused: 'text-warning',
}

// Badge classes conhecidas por adapter. Fallback neutro pra IDs não-mapeados
// (alinhado a `formatAdapterType` em `display.ts` — adapter_type é texto livre
// no backend desde VEC-319).
const ADAPTER_BADGE_CLASSES: Partial<Record<AgentAdapterType, string>> = {
  claude_code: 'bg-primary/15 text-primary border-primary/30',
  codex: 'bg-info/15 text-info border-info/30',
  shell: 'bg-warning/15 text-warning border-warning/30',
  webhook: 'bg-success/15 text-success border-success/30',
  internal: 'border-primary/40 bg-primary/10 text-primary',
  gemini: 'border-info/40 bg-info/10 text-info',
  anthropic: 'border-warning/40 bg-warning/10 text-warning',
  ollama: 'border-success/40 bg-success/10 text-success',
  huggingface: 'border-warning/40 bg-warning/10 text-warning',
}

const ADAPTER_BADGE_FALLBACK = 'bg-muted text-muted-foreground border-border'

export function adapterTypeBadgeClassOf(adapter: AgentAdapterType): string {
  return ADAPTER_BADGE_CLASSES[adapter] ?? ADAPTER_BADGE_FALLBACK
}

/** @deprecated use `adapterTypeBadgeClassOf` — Record fixo não cobre adapter types novos. */
export const adapterTypeBadgeClass: Partial<Record<AgentAdapterType, string>> =
  ADAPTER_BADGE_CLASSES

// Specialty label — falls back to the raw slug for unknown specialties.
export function specialtyLabel(specialtyId: string): string {
  const labels: Record<string, string> = {
    'email-monitoring': 'Email',
    'code-review': 'Code Review',
    'qa-testing': 'QA',
    'research': 'Research',
    'orchestration': 'Orquestrador',
  }
  return labels[specialtyId] ?? specialtyId
}

// Padrão canônico de badge da convenção §7: `bg-{token}/20 text-{token} border-{token}/30`.
export const badgeClassByToken: Record<SemanticToken, string> = {
  success: 'bg-success/20 text-success border-success/30',
  warning: 'bg-warning/20 text-warning border-warning/30',
  destructive: 'bg-destructive/20 text-destructive border-destructive/30',
  info: 'bg-info/20 text-info border-info/30',
  muted: 'bg-muted text-muted-foreground border-border',
  primary: 'bg-primary/20 text-primary border-primary/30',
}

export function agentStatusBadgeClass(status: AgentStatus): string {
  return badgeClassByToken[agentStatusToken[status]]
}

// -----------------------------------------------------------------------------
// Tasks
// -----------------------------------------------------------------------------

export const TASK_STATUSES: TaskStatus[] = [
  'backlog',
  'queued',
  'in_progress',
  'review',
  'done',
  'blocked',
  'errored',  // Wave 1B (AUDIT-011): era 'skipped', alinhado com DB CHECK
]

export const taskStatusLabel: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  queued: 'Na fila',
  in_progress: 'Em progresso',
  review: 'Em revisão',
  done: 'Concluída',
  blocked: 'Bloqueada',
  errored: 'Erro',  // Wave 1B (AUDIT-011)
}

export const taskStatusToken: Record<TaskStatus, SemanticToken> = {
  backlog: 'muted',
  queued: 'info',
  in_progress: 'primary',
  review: 'warning',
  done: 'success',
  blocked: 'destructive',
  errored: 'destructive',  // Wave 1B (AUDIT-011) — erro também usa destructive
}

export function taskStatusBadgeClass(status: TaskStatus): string {
  return badgeClassByToken[taskStatusToken[status]]
}

// ⚠️ DELETADO em 2026-05-18 (W15.2): `TASK_OPERATION_TYPES` hardcoded violava
// Regra de Ouro #2 (NO HARDCODE). Backend tem catalog real em
// `operation_types_catalog` (47 rows). Use `useOperationTypesCatalog()` em
// `src/lib/queries/agentSkills.ts`. O map de labels abaixo é só fallback
// pra humanizar slugs em badges/cards — não é source of truth.

// Labels conhecidos por operation type. Como `TaskOperationType` aceita string
// livre (executors managed entram sem migration), usamos `Partial` + helper
// para cair num fallback amigável quando aparece um ID novo.
const TASK_OPERATION_TYPE_LABELS: Partial<Record<TaskOperationType, string>> = {
  orchestration: 'Orquestração',
  code_generation: 'Geração de código',
  code_review: 'Revisão de código',
  research: 'Pesquisa / síntese',
  document_generation: 'Geração de documento',
  qa_testing: 'QA / Testes',
  email_lead: 'Lead de e-mail',
  'freight-quotation': 'Cotação de frete',
  'freight-quotation-approval': 'Aprovação de caminhão',
  'route-cost-calculation': 'Rota e custo',
  'crm-fill-precheck': 'CRM precheck',
  'crm-fill-finalize': 'CRM finalização',
  'crm-fill': 'Preenchimento CRM',
  'oracle-research': 'Pesquisa Oracle',
  'oracle-extract': 'Oracle · extração',
  'oracle-report': 'Oracle · relatório',
  'oracle-rag': 'Oracle · RAG',
  'oracle-vision': 'Oracle · visão',
  'oracle-summarize': 'Oracle · resumo',
  'financial-audit': 'Auditoria Financeira',
  'financial-bookkeeping': 'Lançamentos Financeiros',
  'conciliacao-backlog': 'Conciliação Backlog',
  'planner-import-ofx': 'Kronos · importar OFX',
  'planner-categorize-pendings': 'Kronos · categorizar pendências',
  other: 'Outro',
}

export function formatTaskOperationType(op: TaskOperationType): string {
  const label = TASK_OPERATION_TYPE_LABELS[op]
  if (label) return label
  // Fallback: humaniza o id cru (`some-new-op` / `some_new_op` → `Some New Op`).
  return op
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** @deprecated use `formatTaskOperationType` — Record fixo não cobre operation types novos. */
export const taskOperationTypeLabel: Partial<Record<TaskOperationType, string>> =
  TASK_OPERATION_TYPE_LABELS

const TASK_OPERATION_TYPE_BADGE_CLASSES: Partial<Record<TaskOperationType, string>> = {
  orchestration: 'bg-info/15 text-info border-info/30',
  code_generation: 'bg-primary/15 text-primary border-primary/30',
  code_review: 'bg-warning/15 text-warning border-warning/30',
  research: 'bg-success/15 text-success border-success/30',
  document_generation: 'bg-info/10 text-info border-info/20',
  qa_testing: 'bg-warning/15 text-warning border-warning/30',
  email_lead: 'bg-info/15 text-info border-info/30',
  'freight-quotation': 'bg-primary/15 text-primary border-primary/30',
  'freight-quotation-approval': 'bg-warning/15 text-warning border-warning/30',
  'route-cost-calculation': 'bg-info/15 text-info border-info/30',
  'crm-fill-precheck': 'bg-primary/15 text-primary border-primary/30',
  'crm-fill-finalize': 'bg-success/15 text-success border-success/30',
  'crm-fill': 'bg-success/15 text-success border-success/30',
  'oracle-research': 'bg-info/15 text-info border-info/30',
  'oracle-extract': 'bg-primary/15 text-primary border-primary/30',
  'oracle-report': 'bg-primary/15 text-primary border-primary/30',
  'oracle-rag': 'bg-primary/15 text-primary border-primary/30',
  'oracle-vision': 'bg-primary/15 text-primary border-primary/30',
  'oracle-summarize': 'bg-primary/15 text-primary border-primary/30',
  'financial-audit': 'bg-success/15 text-success border-success/30',
  'financial-bookkeeping': 'bg-success/15 text-success border-success/30',
  'conciliacao-backlog': 'bg-success/15 text-success border-success/30',
  other: 'bg-muted text-muted-foreground border-border',
}

const TASK_OPERATION_TYPE_BADGE_FALLBACK = 'bg-muted text-muted-foreground border-border'

export function taskOperationTypeBadgeClassOf(op: TaskOperationType): string {
  return TASK_OPERATION_TYPE_BADGE_CLASSES[op] ?? TASK_OPERATION_TYPE_BADGE_FALLBACK
}

/** @deprecated use `taskOperationTypeBadgeClassOf` — Record fixo não cobre operation types novos. */
export const taskOperationTypeBadgeClass: Partial<Record<TaskOperationType, string>> =
  TASK_OPERATION_TYPE_BADGE_CLASSES

// -----------------------------------------------------------------------------
// Routines
// -----------------------------------------------------------------------------

export const routineStatusLabel: Record<RoutineStatus, string> = {
  active: 'Ativo',
  paused: 'Pausado',
  error: 'Erro',
}

export function routineStatusBadgeClass(status: RoutineStatus): string {
  if (status === 'active') return 'border-success/30 bg-success/10 text-success'
  if (status === 'error') return 'border-destructive/30 bg-destructive/10 text-destructive'
  return ''
}

// -----------------------------------------------------------------------------
// Projects
// -----------------------------------------------------------------------------

export const projectStatusLabel: Record<ProjectStatus, string> = {
  backlog: 'Backlog',
  planned: 'Planejado',
  in_progress: 'Em andamento',
  completed: 'Concluído',
  canceled: 'Cancelado',
}

export function projectStatusBadgeClass(status: ProjectStatus): string {
  if (status === 'in_progress') return 'border-info/30 bg-info/10 text-info'
  if (status === 'completed') return 'border-success/30 bg-success/10 text-success'
  if (status === 'canceled') return 'border-destructive/30 bg-destructive/10 text-destructive'
  if (status === 'planned') return 'border-primary/30 bg-primary/10 text-primary'
  return ''
}

// -----------------------------------------------------------------------------
// Runs
// -----------------------------------------------------------------------------

export const runStatusLabel: Record<RunStatus, string> = {
  running: 'Em execução',
  succeeded: 'Sucesso',
  failed: 'Falhou',
  cancelled: 'Cancelado',
}

export function runStatusBadgeClass(status: RunStatus): string {
  if (status === 'running') return 'border-info/30 bg-info/10 text-info'
  if (status === 'succeeded') return 'border-success/30 bg-success/10 text-success'
  if (status === 'failed') return 'border-destructive/30 bg-destructive/10 text-destructive'
  return ''
}

// -----------------------------------------------------------------------------
// Date / time / duration formatters
// -----------------------------------------------------------------------------

/** "dd/mm hh:mm" — used in run lists and workspace timelines. */
export function formatDatetime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** "hh:mm:ss" — used in transcript entry timestamps. */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** "dd/mm/yyyy" — used in project dates. */
export function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** "Xh atrás" / "Em Xd" — used in routine next/last run cells. */
export function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const h = Math.floor(Math.abs(diff) / 3_600_000)
  const future = diff < 0
  if (h < 1) return future ? 'Em instantes' : 'Agora'
  if (h < 24) return future ? `Em ${h}h` : `${h}h atrás`
  const d = Math.floor(h / 24)
  return future ? `Em ${d}d` : `${d}d atrás`
}

/**
 * Parseia timestamps de connector sessions / histórico (Regra de Ouro #3).
 * Preferir ISO-8601 no wire; tolera unix legado e valores inválidos — nunca lançar.
 */
export function parseSessionTimestamp(value: unknown): Date | null {
  if (value == null) return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? null : date
  }
  const raw = String(value).trim()
  if (!raw) return null
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "há 3 minutos" — retorna "—" se o valor não for uma data válida. */
export function formatRelativeDistanceToNow(value: unknown): string {
  const date = parseSessionTimestamp(value)
  if (!date) return '—'
  try {
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR })
  } catch {
    return '—'
  }
}

/** "1.2k" compact notation for token counts. */
export function formatCompactNumber(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
}

/** Human-readable duration from milliseconds: "42ms" / "3.1s" / "2m 15s". */
export function formatDuration(ms: number | null): string {
  if (!ms) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`
}

export function formatBurn(rate: number): string {
  if (rate < 1) return '<1 tok/s'
  if (rate < 1000) return `${Math.round(rate)} tok/s`
  return `${(rate / 1000).toFixed(1)}k tok/s`
}

export function formatBudget(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K`
  return String(tokens)
}

// -----------------------------------------------------------------------------
// RAG / Knowledge Base
// -----------------------------------------------------------------------------

export const RAG_CATEGORIAS: RagDocumentCategoria[] = [
  'manual',
  'procedimento',
  'contrato',
  'tabela',
  'email',
  'outro',
]

export const ragCategoriaLabel: Record<RagDocumentCategoria, string> = {
  manual: 'Manual',
  procedimento: 'Procedimento',
  contrato: 'Contrato',
  tabela: 'Tabela',
  email: 'E-mail',
  outro: 'Outro',
}

export const RAG_DEPARTAMENTOS: RagDocumentDepartamento[] = [
  'operacao',
  'comercial',
  'financeiro',
  'rh',
  'juridico',
  'ti',
]

export const ragDepartamentoLabel: Record<RagDocumentDepartamento, string> = {
  operacao: 'Operação',
  comercial: 'Comercial',
  financeiro: 'Financeiro',
  rh: 'RH',
  juridico: 'Jurídico',
  ti: 'TI',
}

export const RAG_CONFIDENCIALIDADES: RagDocumentConfidencialidade[] = [
  'publica',
  'interna',
  'restrita',
]

export const ragConfidencialidadeLabel: Record<
  RagDocumentConfidencialidade,
  string
> = {
  publica: 'Pública',
  interna: 'Interna',
  restrita: 'Restrita',
}

export const ragDocumentStatusLabel: Record<RagDocumentStatus, string> = {
  uploaded: 'Enviado',
  processing: 'Processando',
  indexed: 'Indexado',
  failed: 'Falhou',
}

export const ragDocumentStatusToken: Record<RagDocumentStatus, SemanticToken> =
  {
    uploaded: 'info',
    processing: 'warning',
    indexed: 'success',
    failed: 'destructive',
  }

export function ragDocumentStatusBadgeClass(status: RagDocumentStatus): string {
  return badgeClassByToken[ragDocumentStatusToken[status]]
}

// -----------------------------------------------------------------------------
// Athena Recommendations (VEC-409)
// -----------------------------------------------------------------------------

export const RECOMMENDATION_KINDS: RecommendationKind[] = [
  'hire_new_agent',
  'add_specialty',
  'rewrite_system_prompt',
  'create_specialty',
  'consolidate_agents',
  'diagnose_gap',
  'suggest_automation',
  'suggest_hire_agent',
]

export const recommendationKindLabel: Record<RecommendationKind, string> = {
  hire_new_agent: 'Contratar agente',
  add_specialty: 'Adicionar especialidade',
  rewrite_system_prompt: 'Reescrever prompt',
  create_specialty: 'Criar especialidade',
  consolidate_agents: 'Consolidar agentes',
  diagnose_gap: 'Diagnóstico SIPOC',
  suggest_automation: 'Sugerir automação',
  suggest_hire_agent: 'Sugerir contratação',
}

/** Kinds executáveis (mutação real após apply manual ou TO-BE POST .../apply). */
export const EXECUTABLE_RECOMMENDATION_KINDS = [
  'hire_new_agent',
  'add_specialty',
  'rewrite_system_prompt',
  'create_specialty',
  'consolidate_agents',
] as const satisfies readonly RecommendationKind[]

export function isExecutableRecommendationKind(
  kind: RecommendationKind,
): kind is (typeof EXECUTABLE_RECOMMENDATION_KINDS)[number] {
  return (EXECUTABLE_RECOMMENDATION_KINDS as readonly string[]).includes(kind)
}

export const RECOMMENDATION_STATUSES: RecommendationStatus[] = [
  'pending',
  'approved',
  'applied',
  'rejected',
  'superseded',
]

export const recommendationStatusLabel: Record<RecommendationStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovada',
  applied: 'Aplicada',
  rejected: 'Rejeitada',
  superseded: 'Substituída',
}

export const recommendationStatusToken: Record<
  RecommendationStatus,
  SemanticToken
> = {
  pending: 'warning',
  approved: 'info',
  applied: 'success',
  rejected: 'destructive',
  superseded: 'muted',
}

export function recommendationStatusBadgeClass(
  status: RecommendationStatus,
): string {
  return badgeClassByToken[recommendationStatusToken[status]]
}

// Cores distintas por kind — sem mapeamento semântico forte, só ajuda a
// distinguir tipos diferentes na lista.
const RECOMMENDATION_KIND_BADGE_CLASSES: Record<RecommendationKind, string> = {
  hire_new_agent: 'bg-primary/15 text-primary border-primary/30',
  add_specialty: 'bg-info/15 text-info border-info/30',
  rewrite_system_prompt: 'bg-warning/15 text-warning border-warning/30',
  create_specialty: 'bg-success/15 text-success border-success/30',
  consolidate_agents: 'bg-primary/15 text-primary border-primary/30',
  diagnose_gap: 'bg-info/15 text-info border-info/30',
  suggest_automation: 'bg-success/15 text-success border-success/30',
  suggest_hire_agent: 'bg-warning/15 text-warning border-warning/30',
}

export function recommendationKindBadgeClass(kind: RecommendationKind): string {
  return RECOMMENDATION_KIND_BADGE_CLASSES[kind]
}

export const ESTIMATED_EFFORTS: EstimatedEffort[] = ['S', 'M', 'L', 'XL']

export const estimatedEffortLabel: Record<EstimatedEffort, string> = {
  S: 'P',
  M: 'M',
  L: 'G',
  XL: 'XG',
}

export const estimatedEffortToken: Record<EstimatedEffort, SemanticToken> = {
  S: 'success',
  M: 'info',
  L: 'warning',
  XL: 'destructive',
}

export function estimatedEffortBadgeClass(effort: EstimatedEffort): string {
  return badgeClassByToken[estimatedEffortToken[effort]]
}

/** Formata confidence ∈ [0,1] como porcentagem ("87%"). */
export function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`
}

/** Alta confidência (≥0.85) — UI destaca + back exige review_notes em rejeição. */
export function isHighConfidence(confidence: number): boolean {
  return confidence >= 0.85
}
